import { Component, h, State, Element, Prop, Event, Watch, type EventEmitter, Listen } from '@stencil/core';
import { makeLogger } from '../../../logger/logger';
import { setFlexComponentPosition } from '../../components-helpers/components-helpers';

const { err } = makeLogger({
  pathToCurrentFile: 'components/result-page/accessibility-toggle/uiaas-search-accessibility-toggle.tsx',
  loggersList: ['err']
});

@Component({
  tag: 'uiaas-search-accessibility-toggle',
  shadow: true,
  styleUrl: 'uiaas-search-accessibility-toggle.scss',
})
export class SearchAccessibilityToggle {
    /**
     * Accessibility mode state
     */
    @State() accessibilityMode: boolean;

    @State() locale: string;

    /**
     * Allow changing position by another components
     */
    @Prop() readonly top?: number;
    @Prop() readonly bottom: number = -6;
    @Prop() readonly left: number = -6;
    @Prop() readonly right?: number;
    @Prop() readonly cmsApiKey: string;
    @Prop() readonly cmsAccessibilityToggleEnabled: boolean;
    @Prop() readonly direction: 'ltr' | 'rtl' = 'ltr';
    @Prop() readonly countryCode: string;
    @Prop() readonly languageCode: string;

    @Element() element;

    @Event({
      eventName: 'uiaas:accessibility',
    })
      accessibilityModeEvent: EventEmitter<any>;//eslint-disable-line @typescript-eslint/no-explicit-any

    @Watch('top')
    @Watch('bottom')
    @Watch('left')
    @Watch('right')
    setPosition() {
        setFlexComponentPosition(this, '.accessibility-button__container');
    }

    @Listen('accessibility-change', { target: 'window' })
    handleGlobalAccessibilityChange(event: CustomEvent) {
        const isActive = event?.detail?.active;
        this.setAccessibilityMode(isActive);
    }

    private getLocalStorage(): boolean {
        try {
          return JSON.parse(localStorage.getItem('se2-accessibility'));
        } catch (e) {
          err(
            'failed to parse localStorage value for se2-accessibility'
          );
          return false;
        }
    }

    private setLocalStorage(param) {
        function paramToString(): any {//eslint-disable-line @typescript-eslint/no-explicit-any
            try {
              return JSON.stringify(param);
            } catch (e) {
              err(
                'failed to stringify localStorage value for se2-accessibility'
              );
              return false;
            }
        }
        localStorage.setItem('se2-accessibility', paramToString());
    }

    connectedCallback() {
        this.setAccessibilityMode(!!this.getLocalStorage());
    }

    componentDidUpdate() {
        this.setAccessibilityMode(!!this.getLocalStorage());
    }

    private setAccessibilityMode(isActive: boolean) {
        this.accessibilityMode = !!isActive;
        if(this.accessibilityMode) {
          document.body.classList.add('se2-accessibility');
          document.documentElement.classList.add('se2-accessibility');
        } else {
          document.body.classList.remove('se2-accessibility');
          document.documentElement.classList.remove('se2-accessibility');
        }

        this.setLocalStorage(isActive);
        this.accessibilityModeEvent.emit(isActive);
    }

    componentDidLoad() {
        this.locale = `${this.languageCode}-${this.countryCode?.toLocaleUpperCase()}`;
        setFlexComponentPosition(this, '.accessibility-button__container');
    }

    render() {
        return this.cmsAccessibilityToggleEnabled && (
          <div class="accessibility-button__container">
            <se-caas
              model="caas-component"
              api-key={this.cmsApiKey}
              target={`{ "locale": "${this.locale ? this.locale : 'en-US'}" }`}
              entry-data='{ "caasId": "accessibility-toggle"}'
              app-context={`{ "currentDir": "${this.direction}" }`}
              class="qds-theme-dark"
            ></se-caas>
          </div>
        );
    }
}
